import dayjs from "dayjs";
import { HistoryItem } from "../pages/vetattendence/components/HistoryModal";

/**
 * Estrutura de um item do QuestionnaireResponse FHIR R5
 */
interface QuestionnaireResponseItem {
  linkId: string;
  text?: string;
  answer?: Array<{
    valueBoolean?: boolean;
    valueDecimal?: number;
    valueInteger?: number;
    valueString?: string;
    [key: string]: any;
  }>;
  item?: QuestionnaireResponseItem[];
}

/**
 * Estrutura básica do QuestionnaireResponse FHIR R5
 */
interface QuestionnaireResponse {
  id?: string;
  authored?: string;
  author?: {
    reference?: string;
    display?: string;
  };
  item?: QuestionnaireResponseItem[];
}

/**
 * Busca recursivamente um item pelo linkId em um QuestionnaireResponse
 * Navega pela hierarquia de grupos e itens
 *
 * @param items - Array de itens do QuestionnaireResponse
 * @param linkId - O linkId a ser buscado (ex: "1.1", "1.3")
 * @returns O item encontrado ou undefined
 */
const findItemByLinkId = (
  items: QuestionnaireResponseItem[] | undefined,
  linkId: string
): QuestionnaireResponseItem | undefined => {
  if (!items) return undefined;

  for (const item of items) {
    // Se encontrou o linkId exato, retorna
    if (item.linkId === linkId) {
      return item;
    }

    // Se o item tem subitens, busca recursivamente
    if (item.item) {
      const found = findItemByLinkId(item.item, linkId);
      if (found) return found;
    }
  }

  return undefined;
};

/**
 * Extrai o valor de um campo específico do QuestionnaireResponse
 *
 * @param questionnaireResponse - O QuestionnaireResponse completo
 * @param linkId - O linkId do campo a ser extraído (ex: "1.1", "1.3")
 * @returns O valor extraído (string, boolean, number) ou undefined
 */
export const extractValue = (
  questionnaireResponse: QuestionnaireResponse,
  linkId: string
): any => {
  const item = findItemByLinkId(questionnaireResponse.item, linkId);

  if (!item || !item.answer || item.answer.length === 0) {
    return undefined;
  }

  const answer = item.answer[0];

  // Retorna o valor conforme o tipo
  return (
    answer.valueString ??
    answer.valueBoolean ??
    answer.valueInteger ??
    answer.valueDecimal ??
    undefined
  );
};

/**
 * Extrai múltiplos valores de um campo (para checkboxes/multi-select)
 *
 * @param questionnaireResponse - O QuestionnaireResponse completo
 * @param linkId - O linkId do campo a ser extraído
 * @returns Array de valores ou array vazio
 */
export const extractMultipleValues = (
  questionnaireResponse: QuestionnaireResponse,
  linkId: string
): string[] => {
  const item = findItemByLinkId(questionnaireResponse.item, linkId);

  if (!item || !item.answer) {
    return [];
  }

  return item.answer
    .map((ans) => ans.valueString)
    .filter((val): val is string => val !== undefined);
};

/**
 * Converte um array de QuestionnaireResponse em histórico formatado
 * para um campo específico
 *
 * @param history - Array de QuestionnaireResponse
 * @param linkId - LinkId do campo a ser extraído
 * @param additionalLinkId - LinkId adicional para informações extras (opcional)
 * @returns Array de HistoryItem formatado
 */
export const convertToHistoryItems = (
  history: QuestionnaireResponse[],
  linkId: string,
  additionalLinkId?: string
): HistoryItem[] => {
  if (!history || history.length === 0) return [];

  const items = history
    ?.map((qr): HistoryItem | null => {
      const value = extractValue(qr, linkId);
      const additionalInfo = additionalLinkId
        ? extractValue(qr, additionalLinkId)
        : undefined;

      // Se não há valor nem informação adicional, não inclui no histórico
      if (value === undefined && !additionalInfo) {
        return null;
      }

      const item: HistoryItem = {
        id: qr.id || "",
        date: qr.authored || "",
        practitioner: qr.author?.display || qr.author?.reference || "",
      };

      // Adiciona value apenas se existir
      if (value !== undefined) {
        item.value = value;
      }

      // Adiciona additionalInfo apenas se existir
      if (
        additionalInfo !== undefined &&
        additionalInfo !== null &&
        additionalInfo !== ""
      ) {
        item.additionalInfo = String(additionalInfo);
      }

      return item;
    })
    .filter((item): item is HistoryItem => item !== null);

  return items;
};

/**
 * Extrai histórico da Queixa Principal (linkId: 1.1)
 */
export const extractMainComplaintHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  return convertToHistoryItems(history, "1.1");
};

/**
 * Extrai histórico da Medicação Contínua (linkId: 1.3)
 */
export const extractMedicationHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  return convertToHistoryItems(history, "1.3");
};

/**
 * Extrai histórico de Exames Recentes (linkId: 1.4)
 */
export const extractExamsHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  return convertToHistoryItems(history, "1.4");
};

/**
 * Extrai histórico de Acesso à Rua (linkId: 1.5 + 1.5.1)
 */
export const extractStreetAccessHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  return convertToHistoryItems(history, "1.5", "1.5.1");
};

/**
 * Extrai histórico de Alimentação (linkId: 1.6)
 */
export const extractFeedingHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  return convertToHistoryItems(history, "1.6");
};

/**
 * Extrai histórico de Vacinação (linkId: 1.7 + 1.7.1)
 */
export const extractVaccinationHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  return convertToHistoryItems(history, "1.7", "1.7.1");
};

/**
 * Extrai histórico de Controle de Ectoparasitas (linkId: 1.8 + 1.8.1)
 */
export const extractEctoparasiteHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  return convertToHistoryItems(history, "1.8", "1.8.1");
};

// =============================================================================
// OBSERVAÇÕES - SISTEMAS CORPORAIS (Grupo 2)
// =============================================================================

/**
 * Extrai histórico de Cardiorrespiratório (linkId: 2.1.1 + 2.1.2)
 * Inclui padrão respiratório e outras observações
 */
export const extractCardiorespiratoryHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  if (!history || history.length === 0) return [];

  return history
    ?.map((qr): HistoryItem | null => {
      // Extrai múltiplos valores do padrão respiratório
      const patterns = extractMultipleValues(qr, "2.1.1");
      const additionalInfo = extractValue(qr, "2.1.2");

      // Se não há valor, não inclui no histórico
      if (patterns.length === 0 && !additionalInfo) {
        return null;
      }

      const item: HistoryItem = {
        id: qr.id || "",
        date: qr.authored || "",
        practitioner: qr.author?.display || qr.author?.reference || "",
      };

      // Adiciona value apenas se houver padrões
      if (patterns.length > 0) {
        item.value = patterns.join(", ");
      }

      // Adiciona additionalInfo apenas se existir
      if (
        additionalInfo !== undefined &&
        additionalInfo !== null &&
        additionalInfo !== ""
      ) {
        item.additionalInfo = String(additionalInfo);
      }

      return item;
    })
    .filter((item): item is HistoryItem => item !== null);
};

/**
 * Extrai histórico de Ingestão de Água (linkId: 2.2)
 */
export const extractWaterIntakeHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  return convertToHistoryItems(history, "2.2");
};

/**
 * Extrai histórico de Digestório (linkId: 2.3.1 + 2.3.2)
 * Inclui padrão alimentar e outras observações
 */
export const extractDigestoryHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  if (!history || history.length === 0) return [];

  return history
    ?.map((qr): HistoryItem | null => {
      const patterns = extractMultipleValues(qr, "2.3.1");
      const additionalInfo = extractValue(qr, "2.3.2");

      if (patterns.length === 0 && !additionalInfo) {
        return null;
      }

      const item: HistoryItem = {
        id: qr.id || "",
        date: qr.authored || "",
        practitioner: qr.author?.display || qr.author?.reference || "",
      };

      if (patterns.length > 0) {
        item.value = patterns.join(", ");
      }

      if (
        additionalInfo !== undefined &&
        additionalInfo !== null &&
        additionalInfo !== ""
      ) {
        item.additionalInfo = String(additionalInfo);
      }

      return item;
    })
    .filter((item): item is HistoryItem => item !== null);
};

/**
 * Extrai histórico de Defecação/Fezes (linkId: 2.4.1 + 2.4.2)
 * Inclui padrão de defecação e outras observações
 */
export const extractDefecationHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  if (!history || history.length === 0) return [];

  return history
    ?.map((qr): HistoryItem | null => {
      const patterns = extractMultipleValues(qr, "2.4.1");
      const additionalInfo = extractValue(qr, "2.4.2");

      if (patterns.length === 0 && !additionalInfo) {
        return null;
      }

      const item: HistoryItem = {
        id: qr.id || "",
        date: qr.authored || "",
        practitioner: qr.author?.display || qr.author?.reference || "",
      };

      if (patterns.length > 0) {
        item.value = patterns.join(", ");
      }

      if (
        additionalInfo !== undefined &&
        additionalInfo !== null &&
        additionalInfo !== ""
      ) {
        item.additionalInfo = String(additionalInfo);
      }

      return item;
    })
    .filter((item): item is HistoryItem => item !== null);
};

/**
 * Extrai histórico de Urogenital (linkId: 2.5.1 + 2.5.2)
 * Inclui presença de secreção e descrição
 */
export const extractUrogenitalHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  return convertToHistoryItems(history, "2.5.1", "2.5.2");
};

/**
 * Extrai histórico de Tipo de Alimentação (linkId: 2.6.1 + 2.6.2)
 * Inclui tipos de alimento e descrição
 */
export const extractFeedingTypeHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  if (!history || history.length === 0) return [];

  return history
    ?.map((qr): HistoryItem | null => {
      const types = extractMultipleValues(qr, "2.6.1");
      const additionalInfo = extractValue(qr, "2.6.2");

      if (types.length === 0 && !additionalInfo) {
        return null;
      }

      const item: HistoryItem = {
        id: qr.id || "",
        date: qr.authored || "",
        practitioner: qr.author?.display || qr.author?.reference || "",
      };

      if (types.length > 0) {
        item.value = types.join(", ");
      }

      if (
        additionalInfo !== undefined &&
        additionalInfo !== null &&
        additionalInfo !== ""
      ) {
        item.additionalInfo = String(additionalInfo);
      }

      return item;
    })
    .filter((item): item is HistoryItem => item !== null);
};

/**
 * Extrai histórico de Neurológico (linkId: 2.7)
 */
export const extractNeurologicalHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  if (!history || history.length === 0) return [];

  return history
    ?.map((qr): HistoryItem | null => {
      const values = extractMultipleValues(qr, "2.7");

      if (values.length === 0) {
        return null;
      }

      return {
        id: qr.id || "",
        date: qr.authored || "",
        value: values.join(", "),
        practitioner: qr.author?.display || qr.author?.reference || "",
      };
    })
    .filter((item): item is HistoryItem => item !== null);
};

/**
 * Extrai histórico de Intolerância ao Exercício (linkId: 2.8)
 */
export const extractExerciseIntoleranceHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  if (!history || history.length === 0) return [];

  return history
    ?.map((qr): HistoryItem | null => {
      const values = extractMultipleValues(qr, "2.8");

      if (values.length === 0) {
        return null;
      }

      return {
        id: qr.id || "",
        date: qr.authored || "",
        value: values.join(", "),
        practitioner: qr.author?.display || qr.author?.reference || "",
      };
    })
    .filter((item): item is HistoryItem => item !== null);
};

/**
 * Extrai histórico de Fêmeas (linkId: 2.9)
 */
export const extractFemalesHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  if (!history || history.length === 0) return [];

  return history
    ?.map((qr): HistoryItem | null => {
      const values = extractMultipleValues(qr, "2.9");

      if (values.length === 0) {
        return null;
      }

      return {
        id: qr.id || "",
        date: qr.authored || "",
        value: values.join(", "),
        practitioner: qr.author?.display || qr.author?.reference || "",
      };
    })
    .filter((item): item is HistoryItem => item !== null);
};

/**
 * Extrai histórico de Vermifugação (linkId: 2.10.1 + 2.10.2)
 */
export const extractDewormingHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  return convertToHistoryItems(history, "2.10.1", "2.10.2");
};

/**
 * Extrai histórico de Observações Gerais da Anamnese (linkId: 2.11)
 */
export const extractAnamnesisObservationsHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  return convertToHistoryItems(history, "2.11");
};

// =============================================================================
// EXAME FÍSICO (Grupo 3)
// =============================================================================

/**
 * Extrai histórico de Exame Físico Geral (linkId: 3.1)
 */
export const extractPhysicalExamHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  return convertToHistoryItems(history, "3.1");
};

/**
 * Extrai histórico de Linfonodos (linkId: 3.2)
 */
export const extractLymphNodesHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  return convertToHistoryItems(history, "3.2");
};

/**
 * Extrai histórico de Ausculta Cardíaca e Pulmonar (linkId: 3.3)
 */
export const extractCardiacPulmonaryAuscultationHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  return convertToHistoryItems(history, "3.3");
};

/**
 * Extrai histórico de Olhos e Mucosa Ocular (linkId: 3.4)
 */
export const extractEyesMucosaHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  return convertToHistoryItems(history, "3.4");
};

/**
 * Extrai histórico de Boca e Mucosa Oral (linkId: 3.5)
 */
export const extractOralMucosaHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  return convertToHistoryItems(history, "3.5");
};

/**
 * Extrai histórico de Condutos Auditivos (linkId: 3.6)
 */
export const extractEarCanalsHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  return convertToHistoryItems(history, "3.6");
};

/**
 * Extrai histórico de Pele e Pelagem (linkId: 3.7.1 + 3.7.2 + 3.7.3)
 * Inclui condições dermatológicas, descrição da pele e outras observações
 */
export const extractSkinHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  if (!history || history.length === 0) return [];

  return history
    ?.map((qr): HistoryItem | null => {
      // Extrai condições dermatológicas (checkboxes)
      const conditions = extractMultipleValues(qr, "3.7.1");
      // Extrai descrição da pele
      const description = extractValue(qr, "3.7.2");
      // Extrai outras observações
      const otherObservations = extractValue(qr, "3.7.3");

      // Se não há nenhuma informação, não inclui no histórico
      if (conditions.length === 0 && !description && !otherObservations) {
        return null;
      }

      const item: HistoryItem = {
        id: qr.id || "",
        date: qr.authored || "",
        practitioner: qr.author?.display || qr.author?.reference || "",
      };

      // Monta o valor combinando condições e descrição
      let valueText = "";
      if (conditions.length > 0) {
        valueText = `Condições: ${conditions.join(", ")}`;
      }
      if (description) {
        valueText += valueText ? `\n${description}` : description;
      }

      if (valueText) {
        item.value = valueText;
      }

      // Adiciona outras observações como additionalInfo
      if (
        otherObservations !== undefined &&
        otherObservations !== null &&
        otherObservations !== ""
      ) {
        item.additionalInfo = String(otherObservations);
      }

      return item;
    })
    .filter((item): item is HistoryItem => item !== null);
};

/**
 * Extrai histórico de Aparelho Locomotor (linkId: 3.8)
 */
export const extractLocomotorSystemHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  return convertToHistoryItems(history, "3.8");
};

/**
 * Extrai histórico de Membros Anteriores (linkId: 3.9)
 */
export const extractAnteriorLimbsHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  return convertToHistoryItems(history, "3.9");
};

/**
 * Extrai histórico de Membros Posteriores (linkId: 3.10)
 */
export const extractPosteriorLimbsHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  return convertToHistoryItems(history, "3.10");
};

// =============================================================================
// SINAIS VITAIS (Grupo 4)
// =============================================================================

/**
 * Extrai histórico de Frequência Cardíaca (linkId: 4.1)
 */
export const extractHeartRateHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  if (!history || history.length === 0) return [];

  return history
    ?.map((qr): HistoryItem | null => {
      const value = extractValue(qr, "4.1");

      if (value === undefined) {
        return null;
      }

      return {
        id: qr.id || "",
        date: qr.authored || "",
        value: `${value} bpm`,
        practitioner: qr.author?.display || qr.author?.reference || "",
      };
    })
    .filter((item): item is HistoryItem => item !== null);
};

/**
 * Extrai histórico de Frequência Respiratória (linkId: 4.2)
 */
export const extractRespiratoryRateHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  if (!history || history.length === 0) return [];

  return history
    ?.map((qr): HistoryItem | null => {
      const value = extractValue(qr, "4.2");

      if (value === undefined) {
        return null;
      }

      return {
        id: qr.id || "",
        date: qr.authored || "",
        value: `${value} mpm`,
        practitioner: qr.author?.display || qr.author?.reference || "",
      };
    })
    .filter((item): item is HistoryItem => item !== null);
};

/**
 * Extrai histórico de Tempo de Preenchimento Capilar (linkId: 4.3)
 */
export const extractCapillaryRefillTimeHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  if (!history || history.length === 0) return [];

  return history
    ?.map((qr): HistoryItem | null => {
      const value = extractValue(qr, "4.3");

      if (value === undefined) {
        return null;
      }

      return {
        id: qr.id || "",
        date: qr.authored || "",
        value: `${value} seg`,
        practitioner: qr.author?.display || qr.author?.reference || "",
      };
    })
    .filter((item): item is HistoryItem => item !== null);
};

/**
 * Extrai histórico de Hidratação (linkId: 4.4)
 */
export const extractHydrationHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  return convertToHistoryItems(history, "4.4");
};

/**
 * Extrai histórico de Temperatura (linkId: 4.5)
 */
export const extractTemperatureHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  if (!history || history.length === 0) return [];

  return history
    ?.map((qr): HistoryItem | null => {
      const value = extractValue(qr, "4.5");

      if (value === undefined) {
        return null;
      }

      return {
        id: qr.id || "",
        date: qr.authored || "",
        value: `${value}°C`,
        practitioner: qr.author?.display || qr.author?.reference || "",
      };
    })
    .filter((item): item is HistoryItem => item !== null);
};

/**
 * Extrai histórico de Peso (linkId: 4.6)
 */
export const extractWeightHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  if (!history || history.length === 0) return [];

  return history
    ?.map((qr): HistoryItem | null => {
      const value = extractValue(qr, "4.6");

      if (value === undefined) {
        return null;
      }

      return {
        id: qr.id || "",
        date: qr.authored || "",
        value: `${value} kg`,
        practitioner: qr.author?.display || qr.author?.reference || "",
      };
    })
    .filter((item): item is HistoryItem => item !== null);
};

// =============================================================================
// DIAGNÓSTICOS E CONDUTAS (Grupo 5)
// =============================================================================

/**
 * Extrai as suspeitas diagnósticas do QuestionnaireResponse mais recente
 * Converte do formato FHIR para o formato do formulário
 * Útil para preencher valores padrão com dados do último atendimento
 *
 * @param history - Array de QuestionnaireResponse ordenados (mais recente primeiro)
 * @returns Array de suspeitas diagnósticas no formato do formulário ou array vazio
 */
export const extractLatestDiagnosticSuspicions = (
  history: QuestionnaireResponse[]
): Array<{
  id: string;
  description: string;
  status?: "confirmed" | "not_confirmed";
  confirmationDescription?: string;
}> => {
  if (!history || history.length === 0) return [];

  // Ordena por data (mais recente primeiro) e pega o primeiro
  const sortedHistory = [...history].sort((a, b) => {
    const dateA = a.authored ? new Date(a.authored).getTime() : 0;
    const dateB = b.authored ? new Date(b.authored).getTime() : 0;
    return dateB - dateA; // Mais recente primeiro
  });

  const latestResponse = sortedHistory[0];
  if (!latestResponse) return [];

  // Busca o grupo 5 (Diagnósticos e Condutas)
  const group5 = findItemByLinkId(latestResponse.item, "5");
  if (!group5 || !group5.item) {
    return [];
  }

  const suspicions: Array<{
    id: string;
    description: string;
    status?: "confirmed" | "not_confirmed";
    confirmationDescription?: string;
  }> = [];

  // Busca todas as suspeitas (itens que começam com "5.1.")
  // O linkId será algo como "5.1.1", "5.1.2", etc. (um para cada suspeita)
  group5.item.forEach((item) => {
    // Verifica se é um item de suspeita (começa com "5.1." e tem subitens)
    if (item.linkId.startsWith("5.1.") && item.item && Array.isArray(item.item)) {
      // Extrai o ID da suspeita (linkId: 5.1.1 dentro do item da suspeita)
      const idItem = item.item.find((subItem) => subItem.linkId === "5.1.1");
      const id =
        idItem?.answer?.[0]?.valueString ||
        `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Extrai a descrição (linkId: 5.1.2) - obrigatório
      const descriptionItem = item.item.find(
        (subItem) => subItem.linkId === "5.1.2"
      );
      const description = descriptionItem?.answer?.[0]?.valueString;

      // Extrai o status (linkId: 5.1.3) - opcional
      const statusItem = item.item.find(
        (subItem) => subItem.linkId === "5.1.3"
      );
      const statusValue = statusItem?.answer?.[0]?.valueString;
      const status =
        statusValue === "confirmed" || statusValue === "not_confirmed"
          ? (statusValue as "confirmed" | "not_confirmed")
          : undefined;

      // Extrai a descrição de confirmação (linkId: 5.1.4) - opcional
      const confirmationItem = item.item.find(
        (subItem) => subItem.linkId === "5.1.4"
      );
      const confirmationDescription =
        confirmationItem?.answer?.[0]?.valueString || undefined;

      // Só adiciona se tiver descrição (obrigatório)
      if (description && description.trim() !== "") {
        const suspicion: {
          id: string;
          description: string;
          status?: "confirmed" | "not_confirmed";
          confirmationDescription?: string;
        } = {
          id,
          description: description.trim(),
        };

        // Adiciona status apenas se existir e for válido
        if (status) {
          suspicion.status = status;
        }

        // Adiciona descrição de confirmação apenas se existir e não for vazio
        if (
          confirmationDescription &&
          confirmationDescription.trim() !== ""
        ) {
          suspicion.confirmationDescription = confirmationDescription.trim();
        }

        suspicions.push(suspicion);
      }
    }
  });

  return suspicions;
};

/**
 * Extrai as condutas clínicas do QuestionnaireResponse mais recente
 * Converte do formato FHIR para o formato do formulário
 * Útil para preencher valores padrão com dados do último atendimento
 *
 * @param history - Array de QuestionnaireResponse ordenados (mais recente primeiro)
 * @returns Array de condutas clínicas no formato do formulário ou array vazio
 */
export const extractLatestClinicalConducts = (
  history: QuestionnaireResponse[]
): Array<{
  id: string;
  description: string;
  status?: "success" | "failed" | "in_progress";
  outcomeDescription?: string;
}> => {
  if (!history || history.length === 0) return [];

  // Ordena por data (mais recente primeiro) e pega o primeiro
  const sortedHistory = [...history].sort((a, b) => {
    const dateA = a.authored ? new Date(a.authored).getTime() : 0;
    const dateB = b.authored ? new Date(b.authored).getTime() : 0;
    return dateB - dateA; // Mais recente primeiro
  });

  const latestResponse = sortedHistory[0];
  if (!latestResponse) return [];

  // Busca o grupo 5 (Diagnósticos e Condutas)
  const group5 = findItemByLinkId(latestResponse.item, "5");
  if (!group5 || !group5.item) {
    return [];
  }

  const conducts: Array<{
    id: string;
    description: string;
    status?: "success" | "failed" | "in_progress";
    outcomeDescription?: string;
  }> = [];

  // Busca todas as condutas (itens que começam com "5.2.")
  // O linkId será algo como "5.2.1", "5.2.2", etc. (um para cada conduta)
  group5.item.forEach((item) => {
    // Verifica se é um item de conduta (começa com "5.2." e tem subitens)
    if (item.linkId.startsWith("5.2.") && item.item && Array.isArray(item.item)) {
      // Extrai o ID da conduta (linkId: 5.2.1 dentro do item da conduta)
      const idItem = item.item.find((subItem) => subItem.linkId === "5.2.1");
      const id =
        idItem?.answer?.[0]?.valueString ||
        `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Extrai a descrição (linkId: 5.2.2) - obrigatório
      const descriptionItem = item.item.find(
        (subItem) => subItem.linkId === "5.2.2"
      );
      const description = descriptionItem?.answer?.[0]?.valueString;

      // Extrai o status (linkId: 5.2.3) - opcional
      const statusItem = item.item.find(
        (subItem) => subItem.linkId === "5.2.3"
      );
      const statusValue = statusItem?.answer?.[0]?.valueString;
      const status =
        statusValue === "success" ||
        statusValue === "failed" ||
        statusValue === "in_progress"
          ? (statusValue as "success" | "failed" | "in_progress")
          : undefined;

      // Extrai a descrição do resultado (linkId: 5.2.4) - opcional
      const outcomeItem = item.item.find(
        (subItem) => subItem.linkId === "5.2.4"
      );
      const outcomeDescription =
        outcomeItem?.answer?.[0]?.valueString || undefined;

      // Só adiciona se tiver descrição (obrigatório)
      if (description && description.trim() !== "") {
        const conduct: {
          id: string;
          description: string;
          status?: "success" | "failed" | "in_progress";
          outcomeDescription?: string;
        } = {
          id,
          description: description.trim(),
        };

        // Adiciona status apenas se existir e for válido
        if (status) {
          conduct.status = status;
        }

        // Adiciona descrição do resultado apenas se existir e não for vazio
        if (outcomeDescription && outcomeDescription.trim() !== "") {
          conduct.outcomeDescription = outcomeDescription.trim();
        }

        conducts.push(conduct);
      }
    }
  });

  return conducts;
};

/**
 * Extrai histórico de Suspeitas Diagnósticas (linkId: 5.1.x)
 * Cria um HistoryItem separado para CADA suspeita diagnóstica
 * Busca itens que começam com "5.1." e agrupa por suspeita
 *
 * Estrutura esperada no QuestionnaireResponse:
 * - linkId: 5.1.1 - ID da Suspeita
 * - linkId: 5.1.2 - Descrição da Suspeita Diagnóstica (obrigatório)
 * - linkId: 5.1.3 - Status da Suspeita (opcional: "confirmed" | "not_confirmed")
 * - linkId: 5.1.4 - Descrição da Confirmação (opcional, sempre disponível independente do status)
 *
 * Comportamento:
 * - Status é opcional (pode não existir)
 * - Descrição de confirmação é sempre extraída quando existir, independente do status
 * - Indicadores visuais: ✓ para "confirmed", ✗ para "not_confirmed", nenhum se não houver status
 */
export const extractDiagnosticSuspicionHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  if (!history || history.length === 0) return [];

  const allItems: HistoryItem[] = [];

  history.forEach((qr) => {
    // Busca todos os itens do grupo 5 (Diagnósticos e Condutas)
    const group5 = findItemByLinkId(qr.item, "5");
    if (!group5 || !group5.item) return;

    // Busca todas as suspeitas (itens que começam com "5.1.")
    group5.item.forEach((item) => {
      if (item.linkId.startsWith("5.1.") && item.item) {
        // Extrai a descrição da suspeita (linkId: 5.1.2) - obrigatório
        const descriptionItem = item.item.find(
          (subItem) => subItem.linkId === "5.1.2"
        );
        if (
          descriptionItem &&
          descriptionItem.answer &&
          descriptionItem.answer.length > 0
        ) {
          const description = descriptionItem.answer[0].valueString;
          if (description) {
            // Extrai o status (linkId: 5.1.3) - opcional, pode não existir
            // Valores possíveis: "confirmed" | "not_confirmed"
            const statusItem = item.item.find(
              (subItem) => subItem.linkId === "5.1.3"
            );
            const status = statusItem?.answer?.[0]?.valueString || undefined;

            // Extrai a descrição de confirmação (linkId: 5.1.4) - opcional
            // Sempre disponível independente do status (pode existir mesmo sem status definido)
            const confirmationItem = item.item.find(
              (subItem) => subItem.linkId === "5.1.4"
            );
            const confirmationDescription =
              confirmationItem?.answer?.[0]?.valueString || undefined;

            // Monta o valor principal com descrição e indicador de status
            let valueText = description;
            if (status === "confirmed") {
              valueText = `✓ ${description}`;
            } else if (status === "not_confirmed") {
              valueText = `✗ ${description}`;
            }
            // Se não houver status, mostra apenas a descrição sem indicador

            const historyItem: HistoryItem = {
              id: qr.id || "",
              date: qr.authored || "",
              practitioner: qr.author?.display || qr.author?.reference || "",
              value: valueText,
            };

            // Adiciona descrição de confirmação como additionalInfo se existir
            // Isso acontece independente do status (pode ter confirmação mesmo sem status definido)
            if (confirmationDescription) {
              historyItem.additionalInfo = `Confirmação: ${confirmationDescription}`;
            }

            allItems.push(historyItem);
          }
        }
      }
    });
  });

  return allItems;
};

/**
 * Extrai histórico de Condutas Clínicas (linkId: 5.2.x)
 * Cria um HistoryItem separado para CADA conduta clínica
 * Busca itens que começam com "5.2." e agrupa por conduta
 *
 * Estrutura esperada no QuestionnaireResponse:
 * - linkId: 5.2.1 - ID da Conduta
 * - linkId: 5.2.2 - Descrição da Conduta Clínica (obrigatório)
 * - linkId: 5.2.3 - Status da Conduta (opcional: "success" | "failed" | "in_progress")
 * - linkId: 5.2.4 - Descrição do Resultado/Desfecho (opcional, sempre disponível independente do status)
 *
 * Comportamento:
 * - Status é opcional (pode não existir)
 * - Descrição do resultado é sempre extraída quando existir, independente do status
 * - Indicadores visuais: ✓ para "success", ✗ para "failed", ⏳ para "in_progress", nenhum se não houver status
 */
export const extractClinicalConductHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  if (!history || history.length === 0) return [];

  const allItems: HistoryItem[] = [];

  history.forEach((qr) => {
    // Busca todos os itens do grupo 5 (Diagnósticos e Condutas)
    const group5 = findItemByLinkId(qr.item, "5");
    if (!group5 || !group5.item) return;

    // Busca todas as condutas (itens que começam com "5.2.")
    group5.item.forEach((item) => {
      if (item.linkId.startsWith("5.2.") && item.item) {
        // Extrai a descrição da conduta (linkId: 5.2.2) - obrigatório
        const descriptionItem = item.item.find(
          (subItem) => subItem.linkId === "5.2.2"
        );
        if (
          descriptionItem &&
          descriptionItem.answer &&
          descriptionItem.answer.length > 0
        ) {
          const description = descriptionItem.answer[0].valueString;
          if (description) {
            // Extrai o status (linkId: 5.2.3) - opcional, pode não existir
            // Valores possíveis: "success" | "failed" | "in_progress"
            const statusItem = item.item.find(
              (subItem) => subItem.linkId === "5.2.3"
            );
            const status = statusItem?.answer?.[0]?.valueString || undefined;

            // Extrai a descrição do resultado (linkId: 5.2.4) - opcional
            // Sempre disponível independente do status (pode existir mesmo sem status definido)
            const outcomeItem = item.item.find(
              (subItem) => subItem.linkId === "5.2.4"
            );
            const outcomeDescription =
              outcomeItem?.answer?.[0]?.valueString || undefined;

            // Monta o valor principal com descrição e indicador de status
            let valueText = description;
            if (status === "success") {
              valueText = `✓ ${description}`;
            } else if (status === "failed") {
              valueText = `✗ ${description}`;
            } else if (status === "in_progress") {
              valueText = `⏳ ${description}`;
            }
            // Se não houver status, mostra apenas a descrição sem indicador

            const historyItem: HistoryItem = {
              id: qr.id || "",
              date: qr.authored || "",
              practitioner: qr.author?.display || qr.author?.reference || "",
              value: valueText,
            };

            // Adiciona descrição do resultado como additionalInfo se existir
            // Isso acontece independente do status (pode ter resultado mesmo sem status definido)
            if (outcomeDescription) {
              historyItem.additionalInfo = `Resultado: ${outcomeDescription}`;
            }

            allItems.push(historyItem);
          }
        }
      }
    });
  });

  return allItems;
};

// =============================================================================
// PRESCRIÇÕES (Grupos 7, 8, 9, 10)
// =============================================================================

/**
 * Extrai histórico de Tratamentos/Medicamentos prescritos (linkId: 7)
 * Cria um HistoryItem separado para CADA tratamento prescrito
 * Indica se cada item foi assinado digitalmente (linkId: 7.5)
 */
export const extractTreatmentsHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  if (!history || history.length === 0) return [];

  const allTreatments: HistoryItem[] = [];

  history.forEach((qr) => {
    // Encontra todos os itens com linkId "7" (tratamentos)
    const treatmentItems = qr.item?.filter((item) => item.linkId === "7");

    if (!treatmentItems || treatmentItems.length === 0) {
      return;
    }

    // Cria um HistoryItem SEPARADO para cada tratamento
    treatmentItems.forEach((treatment) => {
      const name =
        treatment.item?.find((i) => i.linkId === "7.2")?.answer?.[0]
          ?.valueString || "";
      const dosage =
        treatment.item?.find((i) => i.linkId === "7.3")?.answer?.[0]
          ?.valueString || "";
      const description =
        treatment.item?.find((i) => i.linkId === "7.4")?.answer?.[0]
          ?.valueString || "";
      const signed =
        treatment.item?.find((i) => i.linkId === "7.5")?.answer?.[0]
          ?.valueBoolean || false;

      if (!name) return; // Pula se não tem nome

      // Monta o valor principal com nome e dosagem
      let valueText = name;
      if (dosage) {
        valueText += ` - ${dosage}`;
      }

      const item: HistoryItem = {
        id: `${qr.id}-${
          treatment.item?.find((i) => i.linkId === "7.1")?.answer?.[0]
            ?.valueString || Date.now()
        }`,
        date: qr.authored || "",
        value: valueText,
        practitioner: qr.author?.display || qr.author?.reference || "",
      };

      // Adiciona descrição como additionalInfo
      if (description) {
        item.additionalInfo = description;
      }

      // Adiciona indicador visual de assinatura
      if (signed) {
        item.value = `🔒 ${item.value}`;
      }

      allTreatments.push(item);
    });
  });

  return allTreatments;
};

/**
 * Extrai histórico de Exames/Procedimentos solicitados (linkId: 8)
 * Cria um HistoryItem separado para CADA exame/procedimento solicitado
 * Indica se cada item foi assinado digitalmente (linkId: 8.4)
 */
export const extractExamsProceduresHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  if (!history || history.length === 0) return [];

  const allExams: HistoryItem[] = [];

  history.forEach((qr) => {
    // Encontra todos os itens com linkId "8" (exames/procedimentos)
    const examItems = qr.item?.filter((item) => item.linkId === "8");

    if (!examItems || examItems.length === 0) {
      return;
    }

    // Cria um HistoryItem SEPARADO para cada exame/procedimento
    examItems.forEach((exam) => {
      const description =
        exam.item?.find((i) => i.linkId === "8.2")?.answer?.[0]?.valueString ||
        "";
      const notes =
        exam.item?.find((i) => i.linkId === "8.3")?.answer?.[0]?.valueString ||
        "";
      const signed =
        exam.item?.find((i) => i.linkId === "8.4")?.answer?.[0]?.valueBoolean ||
        false;

      if (!description) return; // Pula se não tem descrição

      // Monta o valor principal
      let valueText = description;

      const item: HistoryItem = {
        id: `${qr.id}-${
          exam.item?.find((i) => i.linkId === "8.1")?.answer?.[0]
            ?.valueString || Date.now()
        }`,
        date: qr.authored || "",
        value: valueText,
        practitioner: qr.author?.display || qr.author?.reference || "",
      };

      // Adiciona notas como additionalInfo
      if (notes) {
        item.additionalInfo = notes;
      }

      // Adiciona indicador visual de assinatura
      if (signed) {
        item.value = `🔒 ${item.value}`;
      }

      allExams.push(item);
    });
  });

  return allExams;
};

/**
 * Extrai histórico de Relatórios (linkId: 9)
 * Cria um HistoryItem separado para CADA relatório gerado
 * Indica se cada item foi assinado digitalmente (linkId: 9.3)
 */
export const extractReportsHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  if (!history || history.length === 0) return [];

  const allReports: HistoryItem[] = [];

  history.forEach((qr) => {
    // Encontra todos os itens com linkId "9" (relatórios)
    const reportItems = qr.item?.filter((item) => item.linkId === "9");

    if (!reportItems || reportItems.length === 0) {
      return;
    }

    // Cria um HistoryItem SEPARADO para cada relatório
    reportItems.forEach((report) => {
      const content =
        report.item?.find((i) => i.linkId === "9.2")?.answer?.[0]
          ?.valueString || "";
      const signed =
        report.item?.find((i) => i.linkId === "9.3")?.answer?.[0]
          ?.valueBoolean || false;

      if (!content) return; // Pula se não tem conteúdo

      // Extrai título ou primeiras palavras do conteúdo para o value
      const firstLine = content.split("\n")[0];
      const previewValue =
        firstLine.length > 80 ? `${firstLine.substring(0, 80)}...` : firstLine;

      const item: HistoryItem = {
        id: `${qr.id}-${
          report.item?.find((i) => i.linkId === "9.1")?.answer?.[0]
            ?.valueString || Date.now()
        }`,
        date: qr.authored || "",
        value: previewValue,
        practitioner: qr.author?.display || qr.author?.reference || "",
      };

      // Adiciona conteúdo completo como additionalInfo se for diferente do preview
      if (content !== firstLine || firstLine.length > 80) {
        item.additionalInfo = content;
      }

      // Adiciona indicador visual de assinatura
      if (signed) {
        item.value = `🔒 ${item.value}`;
      }

      allReports.push(item);
    });
  });

  return allReports;
};

/**
 * Extrai histórico de Orientações ao Tutor (linkId: 10)
 */
export const extractGuidelinesHistory = (
  history: QuestionnaireResponse[]
): HistoryItem[] => {
  return convertToHistoryItems(history, "10");
};
